import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Eye, FileText, FileCode, Paperclip, Send, Ban } from 'lucide-react';
import { Spinner } from '../cardStyles';
import ModalContext from '../../../../../contexts/ModalContext';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import { STATUS_COLORS, mapStatus } from '../../invoiceStatus';
import { useEmissionPolling } from '../useEmissionPolling';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
  RowAction,
} from '../../../../../components/StandardPage';

const invoiceScreenKey = 'tax_invoice';
const PAGE_SIZE = 50;

const Badge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${props => STATUS_COLORS[props.type]?.bg || '#f2f2f2'};
  color: ${props => STATUS_COLORS[props.type]?.text || '#666'};
  white-space: nowrap;
`;

const Dot = styled.span<{ type: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${props => STATUS_COLORS[props.type]?.dot || '#999'};
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #9B111E;
  padding: 20px;
  width: 100%;
`;

const formatInvoiceNumber = (row: any) =>
  row.serie && row.numeroNF
    ? `${row.serie}.${String(row.numeroNF).padStart(6, '0')}`
    : '—';

const getInvoiceTotal = (row: any) => {
  const prods = row.InvoiceProduct?.reduce((s: number, p: any) => s + Number(p.valorTotal || 0), 0) || 0;
  const servs = row.InvoiceService?.reduce((s: number, p: any) => s + Number(p.valorTotal || 0), 0) || 0;
  return Number(row.valorTotalNota || (prods + servs));
};

const extractInvoicePage = (data: any): { list: any[]; totalValue: number } => {
  const list = Array.isArray(data) ? data : (data?.data || []);
  const totalValue = Array.isArray(data) ? data.length : (data?.total ?? list.length);
  return { list, totalValue };
};

const InvoiceTable: React.FC = () => {
  const { setModalActive } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<any[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{ id: number; label: string } | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const loadedCountRef = useRef(0);

  const { refresh, search, appliedFilters } = useOutletContext<{ refresh?: boolean; search?: string; appliedFilters?: any[] }>() ?? {};

  const buildParams = useCallback((take: number, skip: number) => {
    const params: any = { take, skip };
    if (search && search.trim() !== '') {
      params.search = search;
    }
    if (appliedFilters && appliedFilters.length > 0) {
      params.filters = JSON.stringify(appliedFilters);
    }
    return params;
  }, [search, appliedFilters]);

  const loadInvoices = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const take = silent ? Math.max(PAGE_SIZE, loadedCountRef.current) : PAGE_SIZE;
      const response = await ApiService.fiscal.getInvoices(buildParams(take, 0));
      const { list, totalValue } = extractInvoicePage(response.data);
      loadedCountRef.current = list.length;
      setInvoices(list);
      setTotal(totalValue);
      if (!silent) setError(null);
    } catch {
      if (!silent) setError('Erro ao carregar as notas fiscais.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [buildParams]);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const response = await ApiService.fiscal.getInvoices(buildParams(PAGE_SIZE, loadedCountRef.current));
      const { list, totalValue } = extractInvoicePage(response.data);
      setInvoices((prev) => {
        const merged = [...prev, ...list];
        loadedCountRef.current = merged.length;
        return merged;
      });
      setTotal(totalValue);
    } catch {
      setFeedback({ message: 'Erro ao carregar mais notas fiscais.', isOpen: true, statusOk: false });
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, setFeedback]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices, refresh]);

  const hasPending = useMemo(
    () => invoices.some((i: any) => i.status === 'EMITINDO'),
    [invoices],
  );

  const syncPending = useCallback(async () => {
    const pendentes = invoices.filter((i: any) => i.status === 'EMITINDO');
    await Promise.allSettled(
      pendentes.map((i: any) => ApiService.fiscal.nfeStatus(i.id)),
    );
    await loadInvoices(true);
  }, [invoices, loadInvoices]);
  useEmissionPolling({ isEmitindo: hasPending, onTick: syncPending });

  const openModal = useCallback((id?: number) => {
    setModalActive(false);
    navigate(id ? `/panel/invoice/edit/${id}` : '/panel/invoice/create');
  }, [navigate, setModalActive]);

  const openView = useCallback((id: number) => {
    navigate(`/panel/invoice/view/${id}`);
  }, [navigate]);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await ApiService.fiscal.cancelar(cancelTarget.id);
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      setCancelTarget(null);
      loadInvoices(true);
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    } finally {
      setCancelLoading(false);
    }
  }, [cancelTarget, setFeedback, loadInvoices]);

  const columns = useMemo<StandardColumn<any>[]>(() => [
    {
      id: 'numeroNF',
      label: 'NF',
      accessor: (row) => formatInvoiceNumber(row),
      cell: (row) => <strong>{formatInvoiceNumber(row)}</strong>,
    },
    {
      id: 'orderId',
      label: 'Pedido',
      accessor: (row) => row.orderId ?? '',
      cell: (row) => <span style={{ color: '#555', fontSize: '13px' }}>{row.orderId || '—'}</span>,
    },
    {
      id: 'chaveAcesso',
      label: 'Chave de acesso',
      accessor: (row) => row.chaveAcesso ?? '',
      cell: (row) => <span style={{ color: '#999', fontSize: '12px' }}>{row.chaveAcesso || '—'}</span>,
    },
    {
      id: 'destinatario',
      label: 'Destinatário',
      accessor: (row) => row.CompanyCliente?.fantasyName || row.CompanyCliente?.companyName || '—',
    },
    {
      id: 'cfop',
      label: 'CFOP',
      accessor: (row) => row.InvoiceProduct?.[0]?.cfop || row.InvoiceService?.[0]?.cfop || '—',
      cell: (row) => {
        const v = row.InvoiceProduct?.[0]?.cfop || row.InvoiceService?.[0]?.cfop || '—';
        return v !== '—' ? <strong style={{ color: '#0085CC' }}>{v}</strong> : <span>—</span>;
      },
    },
    {
      id: 'valorTotal',
      label: 'Valor total',
      accessor: (row) => getInvoiceTotal(row),
      cell: (row) => {
        const v = getInvoiceTotal(row);
        return <strong>{v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—'}</strong>;
      },
      exportValue: (row) => {
        const v = getInvoiceTotal(row);
        return v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—';
      },
    },
    {
      id: 'createdAt',
      label: 'Emissão',
      accessor: (row) => row.createdAt ?? '',
      cell: (row) => <>{row.createdAt ? new Date(row.createdAt).toLocaleDateString('pt-BR') : '—'}</>,
      exportValue: (row) => (row.createdAt ? new Date(row.createdAt).toLocaleDateString('pt-BR') : '—'),
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (row) => mapStatus(row.status),
      cell: (row) => {
        const label = mapStatus(row.status);
        return (
          <Badge type={label}>
            <Dot type={label} />
            {label}
          </Badge>
        );
      },
    },
  ], []);

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(invoiceScreenKey, columns);

  const buildPrimaryAction = (row: any): RowAction => {
    const { id, status, serie, numeroNF } = row;
    const label = serie && numeroNF ? `${serie}.${String(numeroNF).padStart(6, '0')}` : `#${id}`;

    if (status === 'RASCUNHO' || status === 'NEGADA') {
      return { key: 'emit', label: 'Emitir', icon: <Send size={16} />, onClick: () => openModal(id) };
    }
    if (status === 'EMITINDO') {
      return { key: 'emitting', label: 'Emitindo…', icon: <Send size={16} />, onClick: () => {}, disabled: true, title: 'Emissão em andamento' };
    }
    if (status === 'EMITIDA') {
      return { key: 'cancel', label: 'Cancelar NF-e', icon: <Ban size={16} />, onClick: () => setCancelTarget({ id, label }) };
    }
    return { key: 'no-action', label: 'Sem ação disponível', icon: <Ban size={16} />, onClick: () => {}, disabled: true };
  };

  if (loading) {
    return (
      <SpinnerWrapper>
        <Spinner size={32} color="#0085CC" />
        <span style={{ marginLeft: '12px', color: '#666' }}>Carregando notas fiscais...</span>
      </SpinnerWrapper>
    );
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={invoices}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        countLabel='notas fiscais'
        totalCount={total}
        hasMore={total !== null && invoices.length < total}
        isLoading={loadingMore}
        onLoadMore={handleLoadMore}
        exportFileName='notas-fiscais'
        emptyMessage='Nenhuma nota fiscal encontrada.'
        rowActions={(row) => [
          buildPrimaryAction(row),
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => openView(row.id) },
          {
            label: 'Abrir DANFE',
            icon: <FileText size={16} />,
            onClick: () => window.open(row.linkDanfe, '_blank', 'noopener'),
            disabled: !row.linkDanfe,
            title: !row.linkDanfe ? 'DANFE indisponível' : undefined,
          },
          {
            label: 'Baixar XML',
            icon: <FileCode size={16} />,
            onClick: () => window.open(row.linkXml, '_blank', 'noopener'),
            disabled: !row.linkXml,
            title: !row.linkXml ? 'XML indisponível' : undefined,
          },
          {
            label: 'Anexos',
            icon: <Paperclip size={16} />,
            onClick: () => {},
            disabled: true,
            title: 'Anexos (em breve)',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={!!cancelTarget}
        title="Cancelar NF-e"
        message={
          cancelTarget
            ? `Tem certeza que deseja cancelar a nota ${cancelTarget.label}? O cancelamento é irreversível e só é permitido em até 24h da autorização.`
            : ''
        }
        actions={[
          { label: 'Voltar', onClick: () => setCancelTarget(null), variant: 'outline' },
          {
            label: cancelLoading ? 'Cancelando…' : 'Cancelar NF-e',
            onClick: handleConfirmCancel,
            variant: 'danger',
          },
        ]}
      />
    </>
  );
};

export default InvoiceTable;
